/**
 * /welcome — first-sign-in landing for caregivers who arrived without
 * a setup code (try-before-buy path). Captures elder name + time zone
 * (the only fields without sensible defaults) then sends them to the
 * Voice page where personalisation begins.
 *
 * RootRedirect routes here when the account exists but elderName is
 * empty. Caregivers with a setup code go to /add-button, which has
 * its own combined claim+elder form.
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useAccount } from '@/hooks/useAccount';
import { useMyMemberships } from '@/hooks/useMyMemberships';
import { Button } from '@/components/ui/Button';
import { AuthLayout } from '@/components/AuthLayout';
import { Spinner } from '@/components/ui/Spinner';
import { ElderProfileFields } from '@/components/ElderProfileFields';
import { HealthConsentCheckbox } from '@/components/HealthConsentCheckbox';
import { saveElderProfileWithConsent } from '@/lib/firestore';
import { detectTimeZone } from '@/lib/timeZone';
import { useAccountSetupFallback } from '@/hooks/useAccountSetupFallback';
import { AccountSetupProblem } from '@/components/AccountSetupProblem';

export function WelcomePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const uid = auth.state === 'signedIn' ? auth.user.uid : null;
  const { memberships, loading: membershipsLoading } = useMyMemberships(uid);
  const accountId = memberships[0]?.id ?? null;
  const { account, loading: accountLoading } = useAccount(accountId);

  const [elderName, setElderName] = useState('');
  const [timeZone, setTimeZone] = useState(detectTimeZone);
  const [healthConsent, setHealthConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form once per account from the first snapshot. The account
  // listener re-emits after signup (a cache/empty frame, then the server doc
  // ~1s later), each a fresh object; reseeding on every emission would
  // overwrite whatever the caregiver has already typed with the stale (usually
  // empty) snapshot value — the field visibly clears mid-typing. Keying on the
  // account id (rather than a plain boolean) keeps the guard correct if the
  // resolved membership ever changes underneath us.
  const seededAccountId = useRef<string | null>(null);
  useEffect(() => {
    if (account && seededAccountId.current !== account.id) {
      seededAccountId.current = account.id;
      setElderName(account.elderName ?? '');
      setTimeZone(account.timeZone || detectTimeZone());
    }
  }, [account]);

  // Escape hatch if scaffolding never completes (e.g. App Check failure at
  // signup) — surfaces an error + retry instead of an infinite spinner.
  const setup = useAccountSetupFallback(auth.state === 'signedIn' && !accountId);

  if (auth.state === 'loading') {
    return (
      <AuthLayout title="Welcome">
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }
  if (auth.state === 'signedOut') {
    return <Navigate to="/login" replace />;
  }

  // ensure_user_profile is awaited at signup, so docs exist server-
  // side; this only covers the one or two frames it takes the local
  // snapshot listeners to deliver them after we mount.
  const provisioning = membershipsLoading || (accountId && accountLoading);

  // Scaffolding never finished (e.g. App Check failure at signup): surface an
  // error + retry BEFORE the spinner branches, so it's reachable whether we're
  // stuck in `provisioning` (listener never delivered) or in `!accountId`
  // (delivered empty). Guarded on `!accountId` so a just-arrived membership can't
  // flash the error before the effect clears `failed`.
  if (setup.failed && !accountId) {
    return (
      <AuthLayout title="Welcome">
        <AccountSetupProblem onRetry={setup.retry} retrying={setup.retrying} />
      </AuthLayout>
    );
  }

  if (provisioning) {
    return (
      <AuthLayout title="Welcome">
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <Spinner />
          <p className="text-sm text-ink-soft">Just a moment while we get everything ready.</p>
        </div>
      </AuthLayout>
    );
  }

  // No account yet — either provisioning is still propagating to a
  // fresh listener (very common right after signup; a brand-new
  // collection-group subscription often emits `[]` once before the
  // server delivers the membership doc) or the user genuinely has no
  // memberships. Either way, don't bounce — bouncing back to `/`
  // restarts RootRedirect, which still sees its own (now-cached) data
  // and sends us straight back here, producing an infinite loop.
  // Sitting on a spinner is safe: the listener will deliver the doc,
  // and we'll re-render with the form.
  if (!accountId) {
    return (
      <AuthLayout title="Welcome">
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <Spinner />
          <p className="text-sm text-ink-soft">Setting up your account…</p>
        </div>
      </AuthLayout>
    );
  }

  // Already named — nothing to do here.
  if (account && account.elderName.trim()) {
    return <Navigate to={`/accounts/${accountId}/voice`} replace />;
  }

  return (
    <AuthLayout title="Welcome to RemindMeVoice">
      <p className="mb-6 text-sm text-ink-soft">
        Tell us a little about the person you're setting this up for. We'll greet
        them by name and use the right time zone for spoken summaries.
      </p>
      <form
        className="flex flex-col gap-4"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!elderName.trim()) {
            setError("Please enter the first name of the person you care for.");
            return;
          }
          if (!healthConsent) return; // belt-and-braces with the disabled button
          setBusy(true);
          setError(null);
          try {
            await saveElderProfileWithConsent(
              accountId,
              { elderName: elderName.trim(), timeZone },
              auth.user.uid,
            );
            navigate(`/accounts/${accountId}/voice`, { replace: true });
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed.');
          } finally {
            setBusy(false);
          }
        }}
      >
        <ElderProfileFields
          elderName={elderName}
          onElderNameChange={setElderName}
          timeZone={timeZone}
          onTimeZoneChange={setTimeZone}
        />
        <HealthConsentCheckbox
          checked={healthConsent}
          onCheckedChange={setHealthConsent}
          idPrefix="welcome"
        />
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        <Button type="submit" disabled={busy || !healthConsent}>
          {busy ? 'Saving…' : 'Continue'}
        </Button>
      </form>
    </AuthLayout>
  );
}
